import { lemmaCandidates } from './lemma';

/**
 * 文本切分：正文 → 段落 → 句子 → 词。
 *
 * 这是「阅读器」与「统计口径」的共同底座，因此放在 shared 里由前后端共用同一份实现：
 * 导入时后端用它落库（paragraphs / sentences / wordCount），阅读时前端用它把句子切成
 * 可点击的词。两边算法不同步的话，句号切分位置与词数会对不上。
 *
 * 设计取舍（都很朴素，刻意不引 NLP 依赖）：
 * 1. **纯函数、零 I/O**：同一段文本永远得到同样的结果，方便缓存与测试。
 * 2. **偏移量双轨**：`charStart/charEnd` 一律相对**传入的文本**。
 *    后端导入时传入整篇正文（偏移是全文偏移），前端渲染时传入单句（偏移是句内偏移）。
 * 3. **不做真正的句法分析**：句号切分只处理英文里最常见的缩写与小数陷阱，
 *    剩余的边界错误由「句子本身可读」兜底 —— 错切一句不会破坏阅读，只会让长难句
 *    分析多跑一次，代价可以接受。
 */

// ---------------------------------------------------------------------------
// 规范化
// ---------------------------------------------------------------------------

/**
 * 把原始文本规范化成可以被稳定切分的形式：
 * - 统一换行（CRLF / CR → LF）、把 NBSP 与全角空格换成普通空格；
 * - 去掉零宽字符（从网页复制正文时经常带进来，会让点击查词选错词）；
 * - 折叠行内多余空白，但**保留段落结构**（连续空行表示分段）；
 * - 智能引号统一成 ASCII 引号，方便前端按字符切词与匹配。
 *
 * ⚠️ 落库的正文必须是本函数处理过的结果：句子的 charStart/charEnd 指向的是
 * **规范化之后**的文本，两边不一致会导致阅读器定位错位。
 */
export function normalizeArticleText(raw: string): string {
  return raw
    .replace(/\r\n?/g, '\n')
    .replace(/[\u00a0\u2000-\u200b\u3000\ufeff]/g, ' ')
    .replace(/[\u2018\u2019\u201b]/g, "'")
    .replace(/[\u201c\u201d\u201f]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ---------------------------------------------------------------------------
// 段落
// ---------------------------------------------------------------------------

export interface TextRange {
  text: string;
  charStart: number;
  charEnd: number;
}

/**
 * 分段。
 *
 * 规则只有一条，但分两种情况：
 * - **文本里有空行** → 只按空行分段，单换行视为段内折行（尊重原文排版）；
 * - **整篇没有空行** → 按单换行分段。
 *
 * 为什么要有第二种：本平台的主要入口是"粘贴正文"，而从网页/Word 复制过来的文本
 * 经常丢掉空行，只剩下一行一段。如果这时还坚持"空行才算分段"，整篇文章会变成
 * 一个巨大的段落，阅读体验直接崩掉。
 *
 * 代价（刻意接受）：真正的"整段折行且无空行"的文本会被切成许多小段。
 * 这种排版在教材正文里很少见，且用户补一个空行就能修好 —— 比反过来好处理得多。
 */
export function splitParagraphs(raw: string): TextRange[] {
  const out: TextRange[] = [];
  const hasBlankLine = /\n[ \t]*\n/.test(raw);
  const re = hasBlankLine ? /\n[ \t]*\n+/g : /\n+/g;
  let cursor = 0;

  const push = (start: number, end: number) => {
    const slice = raw.slice(start, end);
    const lead = slice.length - slice.trimStart().length;
    const text = slice.trim();
    if (text.length > 0) {
      out.push({ text, charStart: start + lead, charEnd: start + lead + text.length });
    }
  };

  let match: RegExpExecArray | null;
  while ((match = re.exec(raw)) !== null) {
    push(cursor, match.index);
    cursor = match.index + match[0].length;
  }
  push(cursor, raw.length);
  return out;
}

// ---------------------------------------------------------------------------
// 句子
// ---------------------------------------------------------------------------

/**
 * 「不构成句末」的句点。
 *
 * 策略是**宁可少切也不要错切**：缩写表只收那些几乎不可能出现在句末的词
 * （称呼、拉丁缩写、月份、星期）。像 `no.`、`p.` 这类有歧义的刻意不收 ——
 * 它们更常见于句末，误判会让人看到半句话。
 */
const ABBREVIATIONS = new Set([
  'mr', 'mrs', 'ms', 'dr', 'prof', 'sr', 'jr', 'st', 'mt',
  'vs', 'etc', 'eg', 'ie', 'cf', 'al', 'fig', 'vol', 'pp', 'approx', 'dept', 'univ',
  'inc', 'ltd', 'corp', 'ed',
  'jan', 'feb', 'mar', 'apr', 'jun', 'jul', 'aug', 'sept', 'sep', 'oct', 'nov', 'dec',
  'mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun',
]);

/** 收尾字符：句末的引号/括号要跟句子一起切走，否则渲染时会留下孤立的引号。 */
const TRAILING_CHARS = '"\'”’)]}»';

function isProtectedPeriod(text: string, dotIndex: number): boolean {
  // 往回取一个"词"（允许内部含点与撇号，用来识别 U.S. / a.m. / i.e.）
  let start = dotIndex;
  while (start > 0 && /[A-Za-z0-9.']/.test(text[start - 1]!)) start -= 1;
  const word = text.slice(start, dotIndex);

  // 内部含点：U.S. / a.m. / i.e. —— 一定不是句末
  if (word.includes('.')) return true;
  if (word.length === 0) return false;

  // 单个字母 + 后面还是大写开头 → 人名缩写 J. K. Rowling
  if (word.length === 1) {
    const after = text.slice(dotIndex + 1).match(/\s*(\S)/);
    if (after && /[A-Z]/.test(after[1]!)) return true;
  }

  return ABBREVIATIONS.has(word.toLowerCase());
}

/**
 * 把一段文本切成句子（保留字符偏移）。
 *
 * 句末判定：`.`/`!`/`?` 之后必须是空白或文本结束；连续终止符（`?!`、`...`）算一个边界；
 * 终止符后紧跟的引号/括号并入前一句。
 */
export function splitSentenceRanges(text: string): TextRange[] {
  const out: TextRange[] = [];

  const push = (start: number, end: number) => {
    const slice = text.slice(start, end);
    const lead = slice.length - slice.trimStart().length;
    const value = slice.trim();
    if (value.length > 0) {
      out.push({ text: value, charStart: start + lead, charEnd: start + lead + value.length });
    }
  };

  let start = 0;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch !== '.' && ch !== '!' && ch !== '?') continue;

    let end = i;
    while (end + 1 < text.length && '.!?'.includes(text[end + 1]!)) end += 1;
    let after = end + 1;
    while (after < text.length && TRAILING_CHARS.includes(text[after]!)) after += 1;

    const next = after < text.length ? text[after]! : '';
    if (next !== '' && !/\s/.test(next)) {
      i = end;
      continue;
    }
    if (ch === '.' && isProtectedPeriod(text, i)) {
      i = end;
      continue;
    }

    push(start, after);
    while (after < text.length && /\s/.test(text[after]!)) after += 1;
    start = after;
    i = after - 1;
  }

  push(start, text.length);
  return out;
}

/** 只要文本、不要偏移时的便捷版本。 */
export function splitSentences(text: string): string[] {
  return splitSentenceRanges(text).map((range) => range.text);
}

// ---------------------------------------------------------------------------
// 词
// ---------------------------------------------------------------------------

/**
 * 词的正则：字母开头，允许内部出现连字符与撇号（`well-known`、`don't`、`it's`）。
 * 连字符词整体作为一个可点击单元 —— 词典里 `well-known` 本身就是词条，
 * 拆成两个词反而会让用户查不到。
 */
const WORD_RE = /[A-Za-z]+(?:['’-][A-Za-z]+)*/g;

export interface Token {
  /** 句内序号（0 起），对应 `article_word_senses.token_index` */
  index: number;
  /** 原文出现形式（保留大小写与撇号） */
  surface: string;
  /** 归一化后的小写形式，也是查词的 key */
  normalized: string;
  /** 词形还原的首选结果（lemma），等价于 `lemmaCandidates(surface)[0]` */
  lemma: string;
  charStart: number;
  charEnd: number;
  /** 是否值得查（长度 ≥ 2 的实词；单字母 a / I 仍会渲染，但会被这里排除） */
  isLookupWorthy: boolean;
}

/**
 * 切词。
 *
 * @param text      要切的文本
 * @param baseOffset 偏移基准：传 0 得到句内偏移；传句子在正文里的位置得到全文偏移。
 */
export function tokenize(text: string, baseOffset = 0): Token[] {
  const out: Token[] = [];
  WORD_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  let index = 0;

  while ((match = WORD_RE.exec(text)) !== null) {
    const surface = match[0];
    const normalized = surface.toLowerCase();
    out.push({
      index,
      surface,
      normalized,
      lemma: lemmaCandidates(surface)[0] ?? normalized,
      charStart: baseOffset + match.index,
      charEnd: baseOffset + match.index + surface.length,
      isLookupWorthy: normalized.replace(/[^a-z]/g, '').length >= 2,
    });
    index += 1;
  }

  return out;
}

/** 词数统计口径：与 `tokenize` 一致，全文统计与阅读器高亮因此不会出现两个数字。 */
export function countWords(text: string): number {
  WORD_RE.lastIndex = 0;
  let count = 0;
  while (WORD_RE.exec(text) !== null) count += 1;
  return count;
}

/**
 * 功能词表。用于难度打分与"薄弱项"判断：这些词谁都认识，
 * 把它们算进「生词率」会把所有人的水平都低估。
 */
const STOPWORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'if', 'so', 'as', 'than', 'then', 'that', 'this', 'these', 'those',
  'i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them',
  'my', 'your', 'his', 'its', 'our', 'their', 'mine', 'yours', 'hers', 'ours', 'theirs',
  'am', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'do', 'does', 'did', 'done', 'have', 'has', 'had', 'having',
  'will', 'would', 'shall', 'should', 'can', 'could', 'may', 'might', 'must', 'ought',
  'in', 'on', 'at', 'by', 'for', 'with', 'without', 'from', 'to', 'of', 'into', 'onto',
  'about', 'above', 'across', 'after', 'against', 'along', 'among', 'around', 'before', 'behind',
  'below', 'beneath', 'beside', 'between', 'beyond', 'during', 'except', 'inside', 'near', 'off',
  'out', 'outside', 'over', 'past', 'since', 'through', 'throughout', 'till', 'toward', 'towards',
  'under', 'until', 'up', 'upon', 'within', 'down', 'there', 'here', 'where', 'when', 'while',
  'who', 'whom', 'whose', 'which', 'what', 'why', 'how', 'whether',
  'not', 'no', 'nor', 'only', 'also', 'too', 'very', 'just', 'even', 'still', 'yet', 'again',
  'more', 'most', 'much', 'many', 'some', 'any', 'all', 'both', 'each', 'every', 'either', 'neither',
  'other', 'another', 'such', 'own', 'same', 'few', 'less', 'least', 'one', 'two', 'three',
  'because', 'although', 'though', 'unless', 'however', 'therefore', 'thus', 'hence', 'moreover',
  'well', 'now', 'ever', 'never', 'always', 'often', 'sometimes', 'usually', 'really', 'quite',
]);

/** 是否是功能词（只看 lemma 与 normalized，两者取命中） */
export function isStopword(word: string): boolean {
  const key = word.trim().toLowerCase();
  if (STOPWORDS.has(key)) return true;
  return STOPWORDS.has(lemmaCandidates(key)[0] ?? key);
}

// ---------------------------------------------------------------------------
// 整篇切分
// ---------------------------------------------------------------------------

export interface SegmentedSentence {
  paraIndex: number;
  /** 段内句序（0 起） */
  sentIndex: number;
  /** 全文句序（0 起），即 `sentences.global_index` */
  globalIndex: number;
  text: string;
  charStart: number;
  charEnd: number;
  tokens: Token[];
  tokenCount: number;
}

export interface SegmentedParagraph {
  paraIndex: number;
  text: string;
  charStart: number;
  charEnd: number;
  sentenceCount: number;
}

export interface SegmentedArticle {
  paragraphs: SegmentedParagraph[];
  /** 扁平句子列表，按 globalIndex 有序 —— 与 `sentences` 表一行一句一一对应 */
  sentences: SegmentedSentence[];
  paragraphCount: number;
  sentenceCount: number;
  wordCount: number;
  /** 平均句长（词/句），难度打分与长难句识别都要用 */
  avgSentenceLength: number;
}

/**
 * 整篇切分。传入的文本必须已经过 {@link normalizeArticleText}。
 *
 * 段落、句子、词三层一次性算完，避免导入流程里对同一段文本反复扫描
 * （一篇 3000 词的文章只需要遍历三遍：段落、句子、词）。
 */
export function segmentArticle(normalizedText: string): SegmentedArticle {
  const paragraphs: SegmentedParagraph[] = [];
  const sentences: SegmentedSentence[] = [];
  let wordCount = 0;
  let globalIndex = 0;

  splitParagraphs(normalizedText).forEach((para, paraIndex) => {
    const ranges = splitSentenceRanges(para.text);
    paragraphs.push({
      paraIndex,
      text: para.text,
      charStart: para.charStart,
      charEnd: para.charEnd,
      sentenceCount: ranges.length,
    });

    ranges.forEach((range, sentIndex) => {
      const tokens = tokenize(range.text, 0);
      wordCount += tokens.length;
      sentences.push({
        paraIndex,
        sentIndex,
        globalIndex,
        text: range.text,
        // 句子偏移以**正文**为基准：para.charStart + 句内相对位置
        charStart: para.charStart + (range.charStart - 0),
        charEnd: para.charStart + (range.charEnd - 0),
        tokens,
        tokenCount: tokens.length,
      });
      globalIndex += 1;
    });
  });

  return {
    paragraphs,
    sentences,
    paragraphCount: paragraphs.length,
    sentenceCount: sentences.length,
    wordCount,
    avgSentenceLength: sentences.length === 0 ? 0 : wordCount / sentences.length,
  };
}
